import { ulid } from 'ulid';

import type { DetectionSource, Resolution, UntilChoice } from '$domain/events';
import type { AppContext } from '$app/bootstrap';
import { matchingPlan } from '$domain/plans';
import type { PlansState } from '$domain/plans';

/**
 * The Catch and the Comeback — the product's core loop (spec §5).
 *
 * Two taps to log a Catch, at most four to a completed Comeback (spec §3.2).
 * That ceiling is why this service is thin: every await between the tap and the
 * log is time the user spends waiting on us instead of noticing.
 */

export interface LoggedCatch {
  catchId: string;
}

/**
 * Tap 2. The Catch is logged here, and a user who stops at this point has
 * completed a full rep of skill one — nothing downstream may treat an
 * unresolved Catch as an incomplete one.
 */
export async function logCatch(
  ctx: AppContext,
  triggerTag: string,
  // `unknown` is the honest default and in v1 is nearly always the truth: the
  // app cannot tell a muscle-memory arrival from a deliberate one, and no
  // latency heuristic will be invented to guess (architecture §2.9).
  detectionSource: DetectionSource = 'unknown',
): Promise<LoggedCatch> {
  const catchId = ulid();
  await ctx.store.append(ctx.events.makeEvent('catch.logged', { catchId, triggerTag, detectionSource }));
  return { catchId };
}

export interface SurfacedPlan {
  planId: string;
  /** The "then" of the if-then plan — what the user committed to doing. */
  action: string;
  /** The user's own words for why it matters, or null when they gave none. */
  anchor: string | null;
}

/**
 * Choose the plan to offer, and record that it was offered.
 *
 * **The surfacing is written here, before the resolution screen is shown** —
 * not from an effect once it is on screen. Spec §6.3 needs every displayed plan
 * to be a fact in the log, and a fire-and-forget write racing the user's next
 * tap is not that: a fast "Leave it here" followed by the app being killed
 * loses the record, and even when it survives it can land *after* the
 * `catch.resolved` it precedes in real life. Writing it on the way in makes
 * both problems impossible rather than unlikely.
 *
 * It costs no tap. The user is mid-flow between tap 2 and tap 3.
 *
 * Returns null when there is no plan to show — a user who has not onboarded, or
 * who archived everything. The resolution screen then shows two outcomes' worth
 * of choice without a redirect to point at, which is honest; inventing a
 * generic suggestion would be the app telling someone what matters to them
 * (spec §13).
 */
export async function surfacePlanFor(
  ctx: AppContext,
  catchId: string,
  plans: PlansState,
  anchors: ReadonlyMap<string, string>,
  at: Date = new Date(),
): Promise<SurfacedPlan | null> {
  const plan = matchingPlan(plans, at);
  if (!plan) return null;

  await ctx.store.append(
    ctx.events.makeEvent('plan.surfaced', {
      planId: plan.planId,
      catchId,
      surfaceContext: plan.context,
    }),
  );

  return {
    planId: plan.planId,
    action: plan.action,
    // The value anchor: the redirect carries its reason, in the user's own
    // words ("reading = your curiosity" — spec §6.1). Null when they wrote
    // none; silence beats a sentence we made up on their behalf.
    anchor: plan.directionRef === null ? null : (anchors.get(plan.directionRef) ?? null),
  };
}

/**
 * Tap 3, and optionally tap 4.
 *
 * `untilChoice` is a **stated intention, never enforcement**: nothing fires when
 * it elapses, there is no alarm and no "time's up" (spec §6.2). It converts an
 * impulse into an articulated decision; that is the whole of its job.
 */
export async function resolveCatch(
  ctx: AppContext,
  catchId: string,
  resolution: Resolution,
  options: { planId?: string; untilChoice?: UntilChoice } = {},
): Promise<void> {
  await ctx.store.append(
    ctx.events.makeEvent('catch.resolved', {
      catchId,
      resolution,
      ...(options.planId !== undefined ? { planId: options.planId } : {}),
      ...(options.untilChoice !== undefined ? { untilChoice: options.untilChoice } : {}),
    }),
  );
}
